import type { ProjectArtifactDetailDto } from '@inference.engine/workspace-api-client/web';
import type { AssetFile } from '@/pages/ProjectDetail/sections/AssetDetail/assetDetail.types';

/**
 * Adapts `ProjectArtifactDetailDto` to the `AssetFile` shape expected by
 * `AssetPreview`. `downloadUrl` is surfaced by workspace-api's getDetail
 * service: for upload-origin rows it forwards the local `project_file`
 * download URL; for attachment-origin file widgets it tries to extract a
 * file URL from the widget payload before falling back to `null` (which
 * triggers AssetPreview's FileFallback).
 */
export function toAssetFile(detail: ProjectArtifactDetailDto): AssetFile {
  const fileType = detail.mimeType ?? 'application/octet-stream';
  const assetType = resolveAssetType(detail.artifactKind, fileType);
  const downloadUrl = typeof detail.downloadUrl === 'string' ? detail.downloadUrl : null;

  return {
    id: detail.id,
    fileName: detail.artifactName,
    fileSize: detail.artifactSize ?? 0,
    fileType,
    assetType,
    status: 'not_applicable',
    createdAt: detail.attachedAt,
    downloadUrl,
    commentCount: 0,
    tags: [],
  };
}

/**
 * Maps the artifact kind / MIME type to the broad `assetType` category that
 * `AssetPreview` switches on. Falls back to `'other'` for upload rows whose
 * MIME doesn't slot cleanly into one of the dedicated branches.
 *
 * Document MIMEs are explicitly whitelisted instead of accepting any
 * `application/*` — the broader pattern swallows `application/octet-stream`
 * (the fallback when MIME is unknown), archives, wasm, etc. which then fail
 * to render as documents. Routing those through `'other'` lets AssetPreview
 * surface its generic-file fallback rather than attempting a wrong render.
 */
const DOCUMENT_MIME_TYPES = new Set<string>([
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/rtf',
  'application/json',
]);

const SPREADSHEET_MIME_TYPES = new Set<string>([
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
]);

function resolveAssetType(
  artifactKind: string,
  fileType: string,
): 'image' | 'video' | 'document' | 'spreadsheet' | 'other' {
  if (artifactKind === 'image' || fileType.startsWith('image/')) return 'image';
  if (artifactKind === 'video' || fileType.startsWith('video/')) return 'video';
  if (SPREADSHEET_MIME_TYPES.has(fileType)) return 'spreadsheet';
  if (DOCUMENT_MIME_TYPES.has(fileType)) return 'document';
  if (fileType.startsWith('text/')) return 'document';
  return 'other';
}
